<template>
  <transition name="show-up">
    <div
      v-if="!closed && simplifiedView"
      class="u-container elevation-6 expanded"
      style="min-width: 450px;"
      >
      <v-toolbar class="status-bar" >
        <v-toolbar-title>{{statusWithTotal}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          @click="close"
          icon>
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <div v-if="total > 0" key="wrapper-list" class="wrapper-list" ref="wrapperList" >
        <u-files-list :filesByState="retryableFilesByState" />
      </div>
      <div class="px-2">
        <v-progress-linear
          v-if="total > 0"
          :value="filesState.completed / total * 100"
          color="success"
          height="6"
          class="pb-4"
          rounded
          />
      </div>

    </div>
    <div
      v-else-if="!closed"
      :class="{expanded: expanded}"
      class="u-container elevation-6" >
      <v-toolbar class="status-bar" >
        <v-toolbar-title>{{status}}<animated-ellipsis v-if="loading"/></v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          @click="toggleExpanded"
          class="expand"
          icon>
          <v-icon>keyboard_arrow_down</v-icon>
        </v-btn>
        <v-btn
          @click="close"
          icon>
          <v-icon>clear</v-icon>
        </v-btn>
      </v-toolbar>
      <div v-if="total > 0" key="wrapper-list" class="wrapper-list" ref="wrapperList" >
        <u-files-list :filesByState="filesByState" />
      </div>
      <div v-else key="wrapper-loader" class="wrapper-loader">
        <v-progress-circular indeterminate class="primary--text" size="80" />
      </div>
    </div>
  </transition>
</template>

<script>
  import UFilesList from './UFilesList.vue'
  import $state from '../upload-state.js'
  import FILES_STATES from '../file-states.js'
  import AnimatedEllipsis from '~/components/common/AnimatedEllipsis.vue'
  import bus from '~/bus.js'
  import { useBusUtils } from '~/commons/busUtils'
  const { BusHandlerManager } = useBusUtils()
  const bhm = new BusHandlerManager()
  export default {
    name: 'u-container',
    components: {AnimatedEllipsis, 'u-files-list': UFilesList},
    props: {
      active: Boolean
    },
    created () {
      let numberOfFiles = 0
      $state.$on('files-added', () => {
        for (let key in this.filesByState) {
          const files = Object.keys(this.filesByState[key])
          if (files.length > 0) {
            numberOfFiles += 1
          }
        }
        if (numberOfFiles !== 0) {
          this.$set(this, 'closed', false)
        }
      })
    },
    data () {
      return {
        closed: true,
        expanded: true,
        scrollDown: false,
        checkUploads: false
      }
    },
    mounted () {
      bhm.listenRoundTripEvents(bus, 'check-upload-idleness', (payload) => {
        return new Promise((resolve, reject) => {
          if (!this.uploadInProgress) {
            return resolve()
          }
          this.checkUploads = resolve
        })
      })
    },
    updated () {
      if (this.scrollDown) {
        this.scrollDown = false
        // hacking time
        this.$nextTick(() => {
          ;(function ($this) {
            setTimeout(() => {
              $this.$refs.wrapperList.scrollTop = $this.$refs.wrapperList.scrollHeight
            }, 0)
          }(this))
        })
      }
    },
    beforeDestroy () {
      $state.$off('uploadStart')
    },
    methods: {
      toggleExpanded (event) {
        if (this.expanded) {
          const listItemHeight = getComputedStyle(this.$refs.wrapperList.childNodes[0].childNodes[0]).height
          this.$refs.wrapperList.style.height = (parseFloat(listItemHeight) * (this.total > 4 ? 4 : this.total) + 16) + 'px'
          ;(() => {})(this.$refs.wrapperList.offsetHeight)
        }
        this.expanded = !this.expanded
      },
      close () {
        this.closed = true
        $state.clearFiles()
      }
    },
    computed: {
      simplifiedView () {
        return $state.isSimplifiedUploader
      },
      files () {
        return $state.files
      },
      filesByState () {
        return $state.filesByState
      },
      retryableFilesByState () {
        const retryableFilesByState = {}
        for (let key in $state.filesByState) {
          if (key === FILES_STATES.error.id || key === FILES_STATES.canceled.id) {
            retryableFilesByState[key] = $state.filesByState[key]
          }
        }
        return retryableFilesByState
      },
      filesState () {
        let filesStates = {
          [FILES_STATES.pending]: 0,
          [FILES_STATES.completed]: 0,
          [FILES_STATES.canceled]: 0,
          [FILES_STATES.error]: 0,
          [FILES_STATES.uploading]: 0
        }

        for (let key in $state.files) {
          const fileState = $state.files[key].state
          filesStates[fileState] += 1
        }
        return filesStates
      },
      total () {
        return Object.keys($state.files).length
      },
      status () {
        const {completed, error, pending, uploading} = this.filesState

        if ($state.folders) {
          return this.$tf('Creating folders')
        }

        if (pending + uploading > 0) {
          const s = (pending + uploading !== 1 ? 's' : '')
          return this.$tf('files file' + s + ' pending', {files: pending + uploading})
        } else if (error > 0) {
          const s = (error !== 1 ? 's' : '')
          return this.$tf('files error' + s + ' ' + (s ? 'have' : 'has') + ' occurred while uploading', {files: error})
        }

        const s = (completed !== 1 ? 's' : '')
        return this.$tf('files file' + s + ' ' + (s ? 'have' : 'has') + ' been uploaded', {files: completed})
      },
      statusWithTotal () {
        const {completed} = this.filesState
        const s = (completed !== 1 ? 's' : '')
        const status = this.$tf('files file' + s + ' ' + (s ? 'have' : 'has') + ' been uploaded', {files: completed})
        return `${status} ${this.$tf('uploaderTotalSuffix', {total: this.total})}`
      },
      loading () {
        const {pending, uploading} = this.filesState

        if ($state.folders) {
          return true
        }

        if (pending + uploading > 0) {
          return true
        }

        return false
      },
      uploadInProgress () {
        return (this.filesState[FILES_STATES.uploading] + this.filesState[FILES_STATES.pending]) > 0
      }
    },
    watch: {
      active (isActive) {
        if (!isActive) {
          this.close()
        }
      },
      files (newValue, oldValue) {
        if (!this.closed && this.expanded && this.scrollDown) {
          return
        }
        const pastFiles = Object.keys(oldValue).length
        const futureFiles = Object.keys(newValue).length
        if (futureFiles > pastFiles) {
          this.closed = false
          this.expanded = true
          this.scrollDown = true
        }
      },
      expanded (expanded) {
        const wrapperListWidth = getComputedStyle(this.$refs.wrapperList).width
        const listItemHeight = getComputedStyle(this.$refs.wrapperList.childNodes[0].childNodes[0]).height
        const maxHeight = (parseFloat(listItemHeight) * (this.total > 4 ? 4 : this.total) + 16) + 'px'
        if (expanded) {
          this.$refs.wrapperList.style.height = maxHeight
          this.$refs.wrapperList.style.width = 'auto'
        } else {
          this.$refs.wrapperList.style.height = maxHeight
          ;(() => {})(this.$refs.wrapperList.offsetHeight)
          this.$refs.wrapperList.style.width = wrapperListWidth
          this.$refs.wrapperList.style.height = '0px'
        }
      },
      uploadInProgress (val) {
        if (!val && typeof this.checkUploads === 'function') {
          this.checkUploads()
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .u-container {
    margin: 0;
  }
  .show-up-enter-active,
  .show-up-leave-active {
    transition: .5s ease-out;
  }

  .show-up-enter,
  .show-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }

  .wrapper-list {
    transition: .5s ease;
    max-height: 240px;
    overflow: auto;
    will-change: height;
    overflow-y: auto;
  }

  .wrapper-loader {
    text-align: center;
    padding: 2rem;
    background-color: white;
  }

  .expand {
    transition: .24s ease;
    transform: rotate(-180deg);

    .expanded & {
      transform: rotate(0deg);
    }
  }

  // .status-bar {
  //   background-color: #323232;
  //   color: #f1f1f1;

  //   .icon {
  //     color: #ddd !important;
  //   }
  // }
</style>



// WEBPACK FOOTER //
// src/plugins/uploader/components/UContainer.vue